import { Paper, Slide, Stack } from "@mui/material";
import {
  BannerCloseButton,
  BannerIconTile,
  BannerImage,
  BannerText,
} from "./BannerParts";
import { StackedOffer } from "./PromoParts";
import {
  BANNER_VARIANTS,
  SLIDE_IN_SHADOW,
  bannerSurfaceSx,
  type BannerVariant,
  type BannerVisual,
} from "./bannerVariants";

type SlideInBannerProps = {
  open: boolean;
  title: string;
  text?: string;
  /** Seconds left on the countdown, shown above the CTA. */
  countdown?: number;
  /** No CTA once the offer is disabled. */
  ctaLabel?: string;
  onCtaClick: () => void;
  onClose: () => void;
  /** Once the closed card has slid away. */
  onExited?: () => void;
  variant: BannerVariant;
  visual?: BannerVisual;
};

/**
 * Small card that slides up in a bottom corner, with no backdrop. Where it
 * sits on the page belongs to the caller.
 */
export const SlideInBanner = ({
  open,
  title,
  text,
  countdown,
  ctaLabel,
  onCtaClick,
  onClose,
  onExited,
  variant,
  visual,
}: SlideInBannerProps) => (
  <Slide
    in={open}
    direction="up"
    mountOnEnter
    unmountOnExit
    onExited={onExited}
  >
    <Paper
      elevation={8}
      role="complementary"
      aria-label={title}
      data-testid="slide-in-banner"
      sx={[
        {
          position: "relative",
          width: { xs: "100%", md: 360 },
          overflow: "hidden",
          boxShadow: SLIDE_IN_SHADOW,
        },
        bannerSurfaceSx(variant, { paper: true }),
      ]}
    >
      {visual?.type === "image" && (
        <BannerImage
          src={visual.src}
          alt={visual.alt}
          width="100%"
          height={140}
        />
      )}
      <Stack spacing={2.5} p={3}>
        {visual?.type === "icon" && (
          <BannerIconTile
            icon={visual.icon}
            tone={BANNER_VARIANTS[variant].iconTone}
            size="small"
          />
        )}
        <BannerText title={title} text={text} variant={variant} size="small" />
        <StackedOffer
          countdown={countdown}
          ctaLabel={ctaLabel}
          onCtaClick={onCtaClick}
          variant={variant}
        />
      </Stack>
      <BannerCloseButton
        onClose={onClose}
        variant={variant}
        sx={{
          position: "absolute",
          top: (theme) => theme.spacing(2.5),
          right: (theme) => theme.spacing(2.5),
        }}
      />
    </Paper>
  </Slide>
);
